import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import Loading from '../Loading';
import Error from '../Error';
import FillInPanel from './FillInPanel';
import FillInResults from './FillInResults';
import Conversation from './Conversation';
import ExerciseLayout from '../shared/ExerciseLayout';
import { fetchConversationFillInExercise, saveExerciseProgress, loadExerciseProgress, resetExerciseProgress, recordExerciseAttempt } from '../../services/api';
import { useExerciseTimer } from '../../hooks/useExerciseTimer';

const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const ConversationFillInExercise = forwardRef(({ topic = "groceries", onBack }, ref) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [conversation, setConversation] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [wordOptions, setWordOptions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(1);
  const [userAnswers, setUserAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const { timeSpent, stop: stopTimer } = useExerciseTimer();

  // Expose saveProgress method to parent component
  useImperativeHandle(ref, () => ({
    saveProgress: async () => {
      try {
        await saveExerciseProgress(topic, 'conversation_fillin', {
          currentQuestionIndex,
          userAnswers,
          showResults
        });
      } catch (err) {
        console.error('Error saving progress from ref:', err);
        throw err;
      }
    }
  }));

  // Fetch conversation from API on component mount
  const fetchExercises = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await fetchConversationFillInExercise({
        topic: topic
      });

      setConversation(data.dialogue);
      setQuestions(data.questions);
      setWordOptions(shuffleArray(data.word_options));

    } catch (err) {
      console.error('Error fetching conversation fill-in exercise:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [topic]);

  useEffect(() => {
    fetchExercises();
  }, [fetchExercises]);

  // Load saved progress after exercises are loaded
  useEffect(() => {
    const loadProgress = async () => {
      if (!loading && questions.length > 0) {
        try {
          const savedProgress = await loadExerciseProgress(topic, 'conversation_fillin');
          if (savedProgress && savedProgress.progress_data) {
            if (savedProgress.progress_data.currentQuestionIndex !== undefined) {
              setCurrentQuestionIndex(savedProgress.progress_data.currentQuestionIndex);
            }
            if (savedProgress.progress_data.userAnswers) {
              setUserAnswers(savedProgress.progress_data.userAnswers);
            }
            if (savedProgress.progress_data.showResults) {
              setShowResults(true);
            }
          }
        } catch (err) {
          console.error('Error loading progress:', err);
        }
      }
    };

    loadProgress();
  }, [loading, questions.length, topic]);

  const handleAnswerDrop = async (questionId, answer) => {
    const newUserAnswers = {
      ...userAnswers,
      [questionId]: answer
    };
    setUserAnswers(newUserAnswers);

    // Uložit progress ihned po výběru odpovědi
    try {
      await saveExerciseProgress(topic, 'conversation_fillin', {
        currentQuestionIndex,
        userAnswers: newUserAnswers,
        showResults: false
      });
    } catch (err) {
      console.error('Error saving progress:', err);
    }
  };

  const next = () => {
    if (currentQuestionIndex < questions.length) {
      const nextIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIndex);
    }
  };

  const back = () => {
    if (currentQuestionIndex > 1) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const submitAnswers = async () => {
    // Stop timer and get final time
    const finalTime = stopTimer();

    // Calculate score and errors
    let correctCount = 0;
    const errors = [];

    questions.forEach(q => {
      const userAnswer = userAnswers[q.id];
      const isCorrect = userAnswer === q.answer;

      if (isCorrect) {
        correctCount++;
      } else {
        errors.push({
          category: 'fill_in',
          question_id: q.id,
          user_answer: userAnswer || '',
          correct_answer: q.answer
        });
      }
    });

    // Record exercise attempt (don't block UI if this fails)
    try {
      await recordExerciseAttempt({
        topic: topic,
        exercise_type: 'conversation_fillin',
        score: correctCount,
        max_score: questions.length,
        time_spent_seconds: finalTime,
        errors: errors.length > 0 ? errors : null
      });
    } catch (trackingError) {
      console.error('Failed to record exercise attempt:', trackingError);
    }

    setShowResults(true);

    // Build userResponses with correct field for backend detection
    const userResponses = {};
    questions.forEach(q => {
      const userAnswer = userAnswers[q.id];
      userResponses[q.id] = {
        answer: userAnswer || '',
        correct: userAnswer === q.answer
      };
    });

    // Save progress with showResults flag
    try {
      await saveExerciseProgress(topic, 'conversation_fillin', {
        currentQuestionIndex,
        userAnswers,
        userResponses,  // Add structured responses for error detection
        showResults: true,
        errors: errors.length > 0 ? errors : null
      });
    } catch (err) {
      console.error('Error saving progress on submit:', err);
    }
  };

  const restart = async () => {
    setUserAnswers({});
    setCurrentQuestionIndex(1);
    setShowResults(false);

    // Reset progress in database
    try {
      await resetExerciseProgress(topic, 'conversation_fillin');
    } catch (err) {
      console.error('Error resetting progress:', err);
    }

    await fetchExercises();
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    }
  };

  // Loading state
  if (loading) {
    return <Loading />;
  }

  // Error state
  if (error) {
    return <Error error={error} />;
  }

  // Results view
  if (showResults) {
    return (
      <FillInResults
        questions={questions}
        userAnswers={userAnswers}
        restart={restart}
        onBack={handleBack}
        topic={topic}
      />
    );
  }

  // Main exercise view - split view with dialog on left, questions on right
  return (
    <ExerciseLayout
      title="Konverzace - Doplňování"
      subtitle="Doplňte správné odpovědi do dialogu"
      color="blue"
      icon="💬"
      progress={{ current: currentQuestionIndex, total: questions.length }}
      onBack={handleBack}
      twoColumn={true}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Dialog - leva strana */}
        <div className="lg:sticky lg:top-24 lg:self-start min-w-0">
          <h5 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3 text-center">Dialog</h5>
          <Conversation conversation={conversation} topic={topic} />
        </div>

        {/* Otázky - pravá strana */}
        <div className="min-w-0">
          <h5 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3 text-center">Doplnovani</h5>
          <FillInPanel
            questions={questions}
            currentQuestionIndex={currentQuestionIndex}
            wordOptions={wordOptions}
            userAnswers={userAnswers}
            onAnswerDrop={handleAnswerDrop}
            onBack={back}
            onNext={next}
            onSubmit={submitAnswers}
            topic={topic}
          />
        </div>
      </div>
    </ExerciseLayout>
  );
});

export default ConversationFillInExercise;
